import { getCatalogEntryList, useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { FACET_LIMIT_SUFFIX } from '@/data/constants/facet';
import { useFacetLimitState } from '@/data/state/useFacetLimitState';
import { ID } from '@/data/types/Basic';
import { ProductFacetEntry, ProductQueryResponse } from '@/data/types/Product';
import { extractFacetsArray } from '@/data/utils/extractFacetsArray';
import {
	elasticFacetToFriendlyUrl,
	extractElasticField,
	isFacetUrlKey,
	orderByToSortSlug,
	SORT_URL_KEY,
} from '@/data/utils/facetUrlMapper';
import { getProductListFacetFilters } from '@/data/utils/getProductListFacetFilters';
import { getProductListQueryParameters } from '@/data/utils/getProductListQueryParameters';
import { mapFacetDataForFacetWidget } from '@/data/utils/mapFacetData';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const dataMap = (data: ProductQueryResponse | undefined) =>
	extractFacetsArray(data)?.map(mapFacetDataForFacetWidget) ?? [];

/**
 * Do not include this in manifest since it is same as GetCatalogEntryList,
 * Only need when someone have this alone on a page, not likely to happen.
 */
export const getFacetNavigation = getCatalogEntryList;

export const useFacetNavigation = (id: ID) => {
	const router = useNextRouter();
	const { query, locale, defaultLocale } = router;

	const { data, error, total, allFacets, selectedFacet } = useCatalogEntryList(id);
	const { toggleLimit, resetLimit } = useFacetLimitState();
	const facets = useMemo(() => dataMap(data), [data]);
	const filteredParams = useMemo(
		() => getProductListQueryParameters({ ...query }, facets),
		[query, facets]
	);
	const facetFilters = useMemo(() => getProductListFacetFilters(filteredParams), [filteredParams]);
	// Synchronous mirror of the applied facets, so rapid clicks build on the latest
	// selection instead of a stale router.query (which updates async after a shallow push).
	const selectedFacetRef = useRef<string[] | null>(null);

	const onPriceRangeChange = useCallback(
		({ minPrice, maxPrice }: { minPrice: number | null; maxPrice: number | null }) => {
			const {
				minPrice: _min,
				maxPrice: _max,
				facet,
				facetLimit: _facetLimit,
				limit,
				offset: _offset,
				marketId,
				stLocId,
				physicalStoreId,
				searchTerm,
				orderBy,
				...rest
			} = filteredParams;
			const baseQuery = { ...rest } as Record<string, string | string[] | number | undefined>;
			if (minPrice !== null || maxPrice !== null) {
				Object.assign(baseQuery, { minPrice, maxPrice });
			}

			const sortSlug =
				typeof orderBy === 'string' && orderBy !== '0' ? orderByToSortSlug(orderBy) : undefined;
			const sortQuery = sortSlug ? { [SORT_URL_KEY]: sortSlug } : {};
			const newQuery = { ...baseQuery, ...sortQuery };

			const pathname = router.asPath.split('?').at(0);
			router.push({ pathname, query: newQuery }, undefined, {
				shallow: true,
			});
		},
		[filteredParams, router]
	);

	const onFacetSelectChange = useCallback(
		(_event: Event, entry: ProductFacetEntry) => {
			// Seed from the ref mid-burst, fall back to the committed selectedFacet on a fresh start.
			const base = selectedFacetRef.current ?? selectedFacet;

			// on facet change, should reset offset(page number); strip internal + legacy params
			const {
				facet,
				facetLimit: _facetLimit,
				limit,
				offset: _offset,
				marketId,
				stLocId,
				physicalStoreId,
				searchTerm,
				orderBy,
				...rest
			} = filteredParams;
			const baseQuery = { ...rest } as Record<string, string | string[] | number | undefined>;
			Object.keys(baseQuery).forEach((k) => {
				if (isFacetUrlKey(k)) delete baseQuery[k];
			});
			const pathname = router.asPath.split('?').at(0);
			const decodedEntries = base.map((f) => extractElasticField(f).replace('.', ''));
			const entryName = entry.extendedData.name?.toLowerCase() ?? '';
			const isToggleOff = base.includes(entry.value) || decodedEntries.includes(entryName);
			const nextElastic = isToggleOff
				? base.filter((v) => {
						if (!!entryName) {
							const decodedSelectedValue = extractElasticField(v).replace('.', '');
							return entryName !== decodedSelectedValue;
						}
						return v !== entry.value;
				  })
				: [...base, entry.value];
			// Store synchronously so the next rapid click reads this updated set, not the stale closure.
			selectedFacetRef.current = nextElastic;

			// Convert each elastic value back to its friendly URL pair and group by key
			const grouped: Record<string, string[]> = {};
			nextElastic.forEach((elastic) => {
				const friendly = elasticFacetToFriendlyUrl(elastic, allFacets);
				if (!friendly) return;
				(grouped[friendly.key] ||= []).push(friendly.value);
			});
			const facetsQuery: Record<string, string | string[]> = Object.fromEntries(
				Object.entries(grouped).map(([key, values]) => [
					key,
					values.length === 1 ? values[0] : values,
				])
			);

			// Sort: only include `ordenarpor` if user picked something other than relevancia (default)
			const sortSlug =
				typeof orderBy === 'string' && orderBy !== '0' ? orderByToSortSlug(orderBy) : undefined;
			const sortQuery = sortSlug ? { [SORT_URL_KEY]: sortSlug } : {};

			// Order matters: rest first, then facets, then sort — facets/sort end up last in URL
			const newQuery = { ...baseQuery, ...facetsQuery, ...sortQuery };
			router.push({ pathname, query: newQuery }, undefined, {
				shallow: true,
			});

			// Return the post-toggle set so the caller can render it optimistically (sync, via ref).
			return nextElastic;
		},
		[filteredParams, router, selectedFacet, allFacets]
	);

	useEffect(() => {
		// router.query is committed now; drop the mirror so the next burst re-seeds from it.
		selectedFacetRef.current = null;
	}, [query]);

	// Expand/collapse a facet group via shared client state instead of the URL. The key
	// (e.g. `manufacturer.raw:-1`) is read by useCatalogEntryList into the products query, so the
	// backend returns all values for that facet — without writing facetLimit to the URL.
	const toggleFacetLimit = useCallback(
		(facetValue: string) => () => {
			const friendlyKey =
				facets.find((f) => f.value === facetValue || f.extendedData?.propertyvalue === facetValue)
					?.extendedData?.propertyvalue ?? facetValue;
			toggleLimit(friendlyKey + FACET_LIMIT_SUFFIX);
		},
		[facets, toggleLimit]
	);

	// Drop expansions when navigating to a different listing, mirroring the old URL-param reset.
	const pathKey = router.asPath.split('?').at(0);
	useEffect(() => {
		resetLimit();
	}, [pathKey, resetLimit]);

	return {
		available: !!data?.facets?.length,
		facets,
		loading: !error && !data,
		error,
		filteredParams,
		facetFilters,
		selectedFacet,
		locale,
		defaultLocale,
		onPriceRangeChange,
		onFacetSelectChange,
		toggleFacetLimit,
		total,
	};
};
